// Timeline page — Phase 3 Slices A + B + C.
//
// Three things layered together:
//   1. Slice A: reverse-chrono event log grouped by hour.
//   2. Slice B: events with a correlation_id collapse into a single card
//      that shows the narrative; clicking expands to show all members.
//   3. Slice C: a "Time Machine" toggle reveals a datetime input that
//      reconstructs entity states at a past timestamp via /state-at.
//      The URL carries `?at=` so a state snapshot is shareable.

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '../context/AuthContext.js';
import {
  events as eventsApi,
  type EventEntry,
  type EventsQuery,
  type StateAtResponse,
} from '../lib/api.js';

type ActorFilter = 'all' | 'user' | 'automation' | 'script' | 'unknown';

function formatHourBucket(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

function actorLabel(ev: EventEntry): string {
  if (ev.actorName) return ev.actorName;
  switch (ev.actorSource) {
    case 'user':
      return 'Home Assistant user';
    case 'automation':
      return 'Automation';
    case 'script':
      return 'Script';
    default:
      return 'Unknown';
  }
}

function actorBadgeClass(source: EventEntry['actorSource']): string {
  switch (source) {
    case 'user':
      return 'bg-blue-500/10 text-blue-600 dark:text-blue-300';
    case 'automation':
      return 'bg-purple-500/10 text-purple-600 dark:text-purple-300';
    case 'script':
      return 'bg-amber-500/10 text-amber-600 dark:text-amber-300';
    default:
      return 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  }
}

function describeTransition(ev: EventEntry): string {
  const name = ev.entityName ?? ev.entityId;
  if (ev.oldState === null && ev.newState !== null) return `${name} → ${ev.newState}`;
  if (ev.oldState !== null && ev.newState === null) return `${name} removed`;
  if (ev.oldState !== null && ev.newState !== null && ev.oldState !== ev.newState) {
    return `${name}: ${ev.oldState} → ${ev.newState}`;
  }
  return `${name} updated`;
}

function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

function setQueryParam(name: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (value === null) params.delete(name);
  else params.set(name, value);
  const qs = params.toString();
  const url = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
  window.history.replaceState(null, '', url);
}

/** Component-private: a single timeline row with optional cluster expansion. */
function TimelineRow({
  ev,
  expanded,
  onToggle,
  members,
}: {
  ev: EventEntry;
  expanded: boolean;
  onToggle: () => void;
  members: EventEntry[] | null;
}) {
  return (
    <li className="rounded-md border border-gray-200 bg-white text-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-start gap-3 px-3 py-2">
        <span className="mt-0.5 w-20 shrink-0 font-mono text-xs text-gray-500 dark:text-gray-400">
          {formatTime(ev.ts)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate">{ev.narrative ?? describeTransition(ev)}</div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {ev.domain} · {ev.entityId}
            {ev.clusterSize > 1 && (
              <button
                type="button"
                onClick={onToggle}
                className="ml-2 text-blue-600 hover:underline dark:text-blue-400"
              >
                {expanded ? 'Hide' : `Show ${ev.clusterSize} underlying events`}
              </button>
            )}
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${actorBadgeClass(ev.actorSource)}`}
          title={`Triggered by ${actorLabel(ev)}`}
        >
          {actorLabel(ev)}
        </span>
      </div>
      {expanded && members && (
        <ul className="border-t border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-950">
          {members.map((m) => (
            <li
              key={m.id}
              className="flex items-start gap-3 py-1 text-xs text-gray-700 dark:text-gray-300"
            >
              <span className="w-20 shrink-0 font-mono text-gray-500 dark:text-gray-400">
                {formatTime(m.ts)}
              </span>
              <span className="truncate">{describeTransition(m)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function TimeMachinePanel({ homeId, atIso }: { homeId: string; atIso: string }) {
  const [data, setData] = useState<StateAtResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    eventsApi
      .stateAt(homeId, atIso)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load snapshot.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [homeId, atIso]);

  if (loading) return <p className="text-sm text-gray-600 dark:text-gray-400">Loading snapshot…</p>;
  if (error)
    return <p className="text-sm text-red-700 dark:text-red-300">Snapshot unavailable: {error}</p>;
  if (!data || data.entities.length === 0)
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400">
        No event history at that timestamp yet.
      </p>
    );

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
      <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">
        Showing reconstructed states at {new Date(data.ts).toLocaleString()} (read-only)
      </p>
      <ul className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
        {data.entities.map((e) => (
          <li
            key={e.entityId}
            className="flex justify-between gap-2 rounded bg-white/70 px-2 py-1 dark:bg-gray-900/70"
          >
            <span className="truncate text-gray-700 dark:text-gray-300">
              {e.entityName ?? e.entityId}
            </span>
            <span className="font-mono text-gray-500 dark:text-gray-400">{e.state ?? '—'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TimelinePage() {
  const { currentHomeId } = useAuth();
  const [, setLocation] = useLocation();
  const [rows, setRows] = useState<EventEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actorFilter, setActorFilter] = useState<ActorFilter>('all');
  const [expandedClusters, setExpandedClusters] = useState<Map<string, EventEntry[]>>(new Map());
  const [atIso, setAtIso] = useState<string | null>(() => getQueryParam('at'));
  void setLocation; // wouter import for future filter routing

  useEffect(() => {
    if (!currentHomeId) return;
    let cancelled = false;
    setLoading(true);
    setRows([]);
    setNextCursor(null);
    const query: EventsQuery = {};
    if (actorFilter !== 'all') query.actorSource = actorFilter;
    eventsApi
      .list(currentHomeId, query)
      .then((res) => {
        if (cancelled) return;
        setRows(res.events);
        setNextCursor(res.nextCursor);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load timeline.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentHomeId, actorFilter]);

  const loadMore = async () => {
    if (!currentHomeId || !nextCursor) return;
    setLoadingMore(true);
    try {
      const query: EventsQuery = { cursor: nextCursor };
      if (actorFilter !== 'all') query.actorSource = actorFilter;
      const res = await eventsApi.list(currentHomeId, query);
      setRows((prev) => [...prev, ...res.events]);
      setNextCursor(res.nextCursor);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleCluster = async (ev: EventEntry) => {
    if (!currentHomeId || !ev.correlationId) return;
    const next = new Map(expandedClusters);
    if (next.has(ev.correlationId)) {
      next.delete(ev.correlationId);
      setExpandedClusters(next);
      return;
    }
    try {
      const res = await eventsApi.cluster(currentHomeId, ev.correlationId);
      next.set(ev.correlationId, res.events);
      setExpandedClusters(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load cluster.');
    }
  };

  const grouped = useMemo(() => {
    const buckets = new Map<string, EventEntry[]>();
    for (const ev of rows) {
      const d = new Date(ev.ts);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}`;
      const list = buckets.get(key);
      if (list) list.push(ev);
      else buckets.set(key, [ev]);
    }
    return Array.from(buckets.entries()).map(([key, items]) => ({
      key,
      label: items[0] ? formatHourBucket(items[0].ts) : key,
      items,
    }));
  }, [rows]);

  const handleAtChange = (val: string) => {
    if (!val) {
      setAtIso(null);
      setQueryParam('at', null);
      return;
    }
    const iso = new Date(val).toISOString();
    setAtIso(iso);
    setQueryParam('at', iso);
  };

  const homeId = currentHomeId;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Timeline</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          What happened in your home, most recent first.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400" htmlFor="actor-filter">
          Trigger:
        </label>
        <select
          id="actor-filter"
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value as ActorFilter)}
        >
          <option value="all">All</option>
          <option value="user">User</option>
          <option value="automation">Automation</option>
          <option value="script">Script</option>
          <option value="unknown">Unknown</option>
        </select>

        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Time machine:</span>
        <input
          type="datetime-local"
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900"
          value={atIso ? new Date(atIso).toISOString().slice(0, 16) : ''}
          onChange={(e) => handleAtChange(e.target.value)}
        />
        {atIso && (
          <button
            type="button"
            onClick={() => handleAtChange('')}
            className="text-sm text-blue-600 hover:underline dark:text-blue-400"
          >
            Jump to now
          </button>
        )}
      </div>

      {atIso && homeId && (
        <div className="mb-6">
          <TimeMachinePanel homeId={homeId} atIso={atIso} />
        </div>
      )}

      {loading && <p className="text-sm text-gray-600 dark:text-gray-400">Loading…</p>}

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400">
          <p className="mb-2 font-medium">No events yet.</p>
          <p>
            The Timeline needs a paired bridge to stream state changes. If you just paired, events
            will show here as they happen.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {homeId &&
          grouped.map((bucket) => (
            <section key={bucket.key}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {bucket.label}
              </h2>
              <ol className="space-y-1">
                {bucket.items.map((ev) => (
                  <TimelineRow
                    key={ev.id}
                    ev={ev}
                    expanded={!!(ev.correlationId && expandedClusters.has(ev.correlationId))}
                    onToggle={() => void toggleCluster(ev)}
                    members={
                      ev.correlationId ? (expandedClusters.get(ev.correlationId) ?? null) : null
                    }
                  />
                ))}
              </ol>
            </section>
          ))}
      </div>

      {nextCursor && !loading && (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {loadingMore ? 'Loading…' : 'Load earlier'}
          </button>
        </div>
      )}
    </div>
  );
}
